/**
 * Client-side image compression — runs in the browser before upload.
 *
 * Why: modern phone cameras produce 5-15 MB photos. They blow past our 5 MB
 * server limit AND the Kubernetes ingress proxy limit, and any deploy window
 * mid-upload kills them. Compressing to ~800 KB JPEG before the upload starts
 * solves all three problems and uploads ~10x faster.
 *
 * Strategy: draw the image onto a canvas capped at maxDim px on the longest
 * side, export as JPEG at the given quality. Works for JPEG / PNG / WebP and
 * HEIC on iOS Safari (which can decode HEIC natively). On browsers that can't
 * decode the input, the original File is returned unchanged.
 *
 * Returns a File (or the original File on any failure) so callers can drop it
 * straight into a FormData append.
 */

export async function compressImageFile(file, { maxDim = 1600, quality = 0.85 } = {}) {
  if (!file || !(file instanceof Blob)) return file;
  // Already tiny — skip compression
  if (file.size <= 600 * 1024) return file;

  try {
    const bitmap = await loadBitmap(file);
    const { width, height } = scaleToFit(bitmap.width, bitmap.height, maxDim);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    // White backing so any transparent PNG/HEIC renders cleanly as JPEG
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality)
    );
    if (!blob) return file;

    // If somehow compression made it BIGGER (rare, tiny PNG icons), keep original
    if (blob.size >= file.size) return file;

    const baseName = (file.name || 'upload').replace(/\.[^.]+$/, '');
    return new File([blob], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch {
    // Any decode/encode failure → just upload the original
    return file;
  }
}

async function loadBitmap(file) {
  // createImageBitmap is the fastest and works for almost everything on modern
  // browsers (including HEIC on iOS Safari 17+).
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(file); } catch { /* fall through */ }
  }
  // Fallback: <img> + objectURL
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

function scaleToFit(w, h, maxDim) {
  if (w <= maxDim && h <= maxDim) return { width: w, height: h };
  const ratio = Math.min(maxDim / w, maxDim / h);
  return { width: Math.round(w * ratio), height: Math.round(h * ratio) };
}
